import http from 'apis/config/http-service';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
} from 'design-system';
// A legacy `src/apis/restaurants.ts` (default export) sits next to the `apis/restaurants/`
// directory; Node/webpack module resolution picks the file over the directory's index.ts for the
// bare `'apis/restaurants'` specifier, so the barrel is unreachable from that path. Import the file
// directly instead (see CLAUDE.md's "legacy directories" gotcha).
import { useCreateRestaurantApi } from 'apis/restaurants/use-create-restaurant-api';
import { LookupRestaurant, useInfiniteLookupRestaurantsApi } from 'apis/sales-lookup';
import { useDebounce } from 'hooks/use-debounce';
import { usePageParams } from 'hooks/use-page-params';
import qs from 'query-string';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FiChevronDown, FiChevronLeft, FiSearch } from 'react-icons/fi';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';

// The lighter create form (2026-09-10): a prospect met on a visit is a UAE
// restaurant until proven otherwise. Country is fixed and shown read-only, the
// branch is auto-created under the restaurant's name with this default address
// (the BE requires an address line), and the phone dial code defaults to +971.
const DEFAULT_COUNTRY_CODE = 'AE';
const DEFAULT_COUNTRY_NAME = 'United Arab Emirates';
const DEFAULT_BRANCH_ADDRESS = 'United Arab Emirates';

interface CreateFormValues {
  name: string;
  contactName: string;
  contactPhone: string; // E.164 from react-phone-number-input
}

// "Who are you meeting?" — full-screen sheet: every restaurant listed
// immediately (search narrows), plus in-place creation for prospects. The
// selection is a pair of URL params so it survives refresh and travels with
// navigation. `autoOpen` lets the log-price gate open the sheet on mount.
export const RestaurantPicker = ({
  autoOpen = false,
  onLight = false,
}: {
  autoOpen?: boolean;
  /** True when rendered on a light-surface background (e.g. the feedback gate) instead of the dark shell header. */
  onLight?: boolean;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure({ defaultIsOpen: autoOpen });
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();
  const restaurantId = Number(params.restaurantId) || undefined;
  const restaurantName = (params.restaurantName as string) || '';

  const [panel, setPanel] = useState<'list' | 'create'>('list');
  const [createInitialName, setCreateInitialName] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);

  const { data, isLoading, isFetching, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteLookupRestaurantsApi(debouncedSearch);
  const restaurants = useMemo(() => (data?.pages ?? []).flatMap((page) => page.data), [data]);

  // Infinite scroll inside the sheet body.
  const bodyRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !isOpen || panel !== 'list') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
      },
      { root: bodyRef.current, rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isOpen, panel, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const select = (restaurant: LookupRestaurant) => {
    setPageParams({ ...getPageParams(), restaurantId: restaurant.id, restaurantName: restaurant.nameEn });
    setPanel('list');
    onClose();
  };

  // Lookup miss after a successful create (BE toasted success, but the read model the picker
  // queries hasn't caught up yet): fall back to the list, pre-searched by the new name, instead of
  // stranding the rep on the populated create form (a second submit would trip ConflictException).
  const lookupMiss = (name: string) => {
    setSearchInput(name);
    setPanel('list');
  };

  const clear = () => {
    const next = getPageParams();
    delete next.restaurantId;
    delete next.restaurantName;
    setPageParams(next);
    onClose();
  };

  const closeSheet = () => {
    setPanel('list');
    onClose();
  };

  return (
    <>
      <Flex
        as='button'
        w='full'
        align='center'
        justify='space-between'
        bg={onLight ? 'white' : 'whiteAlpha.100'}
        border='1px solid'
        borderColor={onLight ? 'gray.200' : 'whiteAlpha.300'}
        borderRadius='12px'
        px='14px'
        py='10px'
        onClick={onOpen}>
        <Box textAlign='start'>
          <Text
            fontSize='10px'
            fontWeight='semibold'
            letterSpacing='0.12em'
            color={onLight ? 'gray.500' : 'whiteAlpha.600'}>
            CUSTOMER
          </Text>
          <Text fontSize='15px' fontWeight='semibold' color={onLight ? 'gray.800' : 'white'} noOfLines={1}>
            {restaurantName || 'No customer selected'}
          </Text>
        </Box>
        <FiChevronDown color={onLight ? 'var(--chakra-colors-gray-500)' : 'var(--chakra-colors-whiteAlpha-700)'} />
      </Flex>

      <Modal isOpen={isOpen} onClose={closeSheet} size='full' scrollBehavior='inside'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize='15px' maxW='480px' mx='auto' w='full'>
            {panel === 'list' ? 'Who are you meeting?' : 'New restaurant'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody ref={bodyRef} pb='24px'>
            <Box maxW='480px' mx='auto'>
              {panel === 'list' ? (
                <ListPanel
                  restaurants={restaurants}
                  isLoading={isLoading}
                  isFetching={isFetching}
                  isFetchingNextPage={isFetchingNextPage}
                  sentinelRef={sentinelRef}
                  searchInput={searchInput}
                  onSearchChange={setSearchInput}
                  selectedId={restaurantId}
                  selectedName={restaurantName}
                  onSelect={select}
                  onClear={clear}
                  onCreate={(name) => {
                    setCreateInitialName(name ?? '');
                    setPanel('create');
                  }}
                />
              ) : (
                <CreatePanel
                  initialName={createInitialName}
                  onBack={() => setPanel('list')}
                  onCreated={select}
                  onLookupMiss={lookupMiss}
                />
              )}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

const ListPanel = ({
  restaurants,
  isLoading,
  isFetching,
  isFetchingNextPage,
  sentinelRef,
  searchInput,
  onSearchChange,
  selectedId,
  selectedName,
  onSelect,
  onClear,
  onCreate,
}: {
  restaurants: LookupRestaurant[];
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  sentinelRef: React.RefObject<HTMLDivElement>;
  searchInput: string;
  onSearchChange: (value: string) => void;
  selectedId?: number;
  selectedName: string;
  onSelect: (restaurant: LookupRestaurant) => void;
  onClear: () => void;
  onCreate: (name?: string) => void;
}) => (
  <>
    <Box
      as='button'
      w='full'
      py='12px'
      mb='14px'
      borderRadius='12px'
      bg='red.600'
      _hover={{ bg: 'red.700' }}
      onClick={() => onCreate()}>
      <Text fontSize='15px' fontWeight='bold' color='white'>
        + New restaurant
      </Text>
    </Box>

    <InputGroup mb='10px'>
      <InputLeftElement pointerEvents='none' h='full'>
        <FiSearch color='var(--chakra-colors-gray-400)' size='15px' />
      </InputLeftElement>
      <Input placeholder='Search restaurants…' value={searchInput} onChange={(e) => onSearchChange(e.target.value)} />
      {isFetching && !isLoading && !isFetchingNextPage && (
        <Flex position='absolute' insetEnd='12px' top='0' bottom='0' align='center'>
          <Spinner size='xs' color='red.500' />
        </Flex>
      )}
    </InputGroup>

    <Text fontSize='10.5px' fontWeight='semibold' letterSpacing='0.1em' color='gray.500' mb='8px'>
      EXISTING ACCOUNTS
    </Text>

    {selectedName && (
      <Box
        as='button'
        w='full'
        textAlign='start'
        px='12px'
        py='10px'
        borderRadius='9px'
        _hover={{ bg: 'gray.50' }}
        onClick={onClear}>
        <Text fontSize='13.5px' fontWeight='semibold' color='red.600'>
          Clear selection ({selectedName})
        </Text>
      </Box>
    )}

    {isLoading ? (
      <Flex justify='center' py='lg'>
        <Spinner size='sm' color='red.500' />
      </Flex>
    ) : restaurants.length ? (
      restaurants.map((restaurant) => {
        const isSelected = restaurant.id === selectedId;
        return (
          <Flex
            key={restaurant.id}
            as='button'
            w='full'
            textAlign='start'
            align='center'
            px='12px'
            py='10px'
            borderRadius='9px'
            bg={isSelected ? 'red.50' : 'transparent'}
            _hover={{ bg: isSelected ? 'red.50' : 'gray.50' }}
            onClick={() => onSelect(restaurant)}>
            <Flex
              w='36px'
              h='36px'
              borderRadius='9px'
              bg='red.50'
              align='center'
              justify='center'
              mr='10px'
              flexShrink={0}>
              <Text fontSize='14px' fontWeight='bold' color='red.600'>
                {restaurant.nameEn?.trim().charAt(0).toUpperCase() || '?'}
              </Text>
            </Flex>
            <Text fontSize='13.5px' fontWeight={isSelected ? 'semibold' : 'medium'} color='gray.800' noOfLines={1}>
              {restaurant.nameEn}
            </Text>
            {isSelected && (
              <Box ml='auto' bg='red.100' px='8px' py='2px' borderRadius='full'>
                <Text fontSize='9px' fontWeight='bold' letterSpacing='0.1em' color='red.700'>
                  CURRENT
                </Text>
              </Box>
            )}
          </Flex>
        );
      })
    ) : (
      <Box py='20px'>
        <Text fontSize='13px' color='gray.500' textAlign='center'>
          No restaurants found
        </Text>
        {searchInput.trim() && (
          // A miss on a typed name is the prospect case — carry the name
          // straight into the create form instead of making the rep retype it.
          <Box
            as='button'
            w='full'
            mt='12px'
            py='10px'
            borderRadius='12px'
            border='1.5px solid'
            borderColor='red.500'
            bg='red.50'
            onClick={() => onCreate(searchInput.trim())}>
            <Text fontSize='13.5px' fontWeight='bold' color='red.600' noOfLines={1}>
              Add &quot;{searchInput.trim()}&quot; as a new restaurant
            </Text>
          </Box>
        )}
      </Box>
    )}

    <Box ref={sentinelRef} h='1px' />
    {isFetchingNextPage && (
      <Flex justify='center' py='10px'>
        <Spinner size='xs' color='red.500' />
      </Flex>
    )}
  </>
);

const CreatePanel = ({
  initialName,
  onBack,
  onCreated,
  onLookupMiss,
}: {
  initialName: string;
  onBack: () => void;
  onCreated: (restaurant: LookupRestaurant) => void;
  onLookupMiss: (name: string) => void;
}) => {
  const { mutate: createRestaurant, isLoading: isCreating } = useCreateRestaurantApi();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateFormValues>({
    mode: 'onChange',
    defaultValues: { name: initialName, contactName: '', contactPhone: '' },
  });

  const onSubmit = (form: CreateFormValues) => {
    const nameEn = form.name.trim();

    createRestaurant(
      {
        body: {
          nameEn,
          countryCode: DEFAULT_COUNTRY_CODE,
          // CmsCreateBranchDto requires fullLine (the branch address) alongside
          // nameEn — omitting it 400s the whole create. The rep doesn't collect
          // an address on a visit, so the country stands in for it.
          branch: { nameEn, fullLine: DEFAULT_BRANCH_ADDRESS },
          admin: { fullName: form.contactName.trim(), phoneNumber: form.contactPhone },
        },
      },
      {
        onSuccess: async () => {
          // The UMS create endpoint (api/v1/restaurants → RestaurantCmsController#createRestaurant)
          // returns Promise<void> — the response body carries no created entity, so there's no `id`
          // to read off it directly. Look the row back up by name + country (sorted newest-first) on
          // the same `restaurants` read model this picker's list already queries — the existing
          // useCreateRestaurantApi hook already assumes the two are the same store, since its own
          // onSuccess invalidates the UMS restaurants list query (getCmsRestaurantsEndpointIdentifier,
          // from use-get-cms-restaurants-api.ts) rather than this endpoint. The conflict check on
          // create guarantees the name+country pair is unique, so the top hit is unambiguous.
          try {
            const { data } = await http.get('restaurants', {
              params: {
                page: 1,
                limit: 1,
                sort: 'id,DESC',
                filter: [`nameEn||$eq||${nameEn}`, `countryCode||$eq||${DEFAULT_COUNTRY_CODE}`],
              },
              paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'none', sort: false }),
            });
            const created = data?.data?.[0];
            if (created?.id) {
              onCreated({ id: created.id, nameEn: created.nameEn ?? nameEn });
              return;
            }
            // Created (the create hook already toasted success) but the read model hasn't caught up
            // yet (e.g. replica lag) — degrade to the list, pre-searched by the new name, instead of
            // stranding the rep on the now-stale create form (a second submit would trip
            // ConflictException). They tap the row as soon as it shows up.
            onLookupMiss(nameEn);
          } catch {
            // The create succeeded (the hook already toasted success) — this recovery read just
            // failed (e.g. network blip). Same degradation as the lookup-miss case above: fall back
            // to the list, pre-searched by the new name, instead of stranding the rep on the
            // now-stale create form (a second submit would trip ConflictException).
            onLookupMiss(nameEn);
          }
        },
      }
    );
  };

  return (
    <Box as='form' onSubmit={handleSubmit(onSubmit)} noValidate>
      <Flex as='button' type='button' align='center' mb='12px' onClick={onBack}>
        <FiChevronLeft />
        <Text fontSize='14px' fontWeight='semibold' color='gray.700'>
          Back
        </Text>
      </Flex>

      <Text fontSize='14px' color='gray.700' mb='14px'>
        Not in the system yet — add them here and they&apos;re selected for this visit.
      </Text>

      <FormControl mb='12px' isRequired isInvalid={!!errors.name}>
        <FormLabel fontSize='12px'>Restaurant name</FormLabel>
        <Input
          placeholder='e.g. Saffron House Rest.'
          {...register('name', {
            required: 'Name is required.',
            validate: (value) => !!value.trim() || 'Name is required.',
          })}
        />
        <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
      </FormControl>

      <FormControl mb='12px' isRequired isInvalid={!!errors.contactName}>
        <FormLabel fontSize='12px'>Contact person</FormLabel>
        <Input
          placeholder='Who are you meeting?'
          {...register('contactName', {
            required: 'Contact person is required.',
            validate: (value) => !!value.trim() || 'Contact person is required.',
          })}
        />
        <FormErrorMessage>{errors.contactName?.message}</FormErrorMessage>
      </FormControl>

      <Controller
        control={control}
        name='contactPhone'
        rules={{
          required: 'Contact phone is required.',
          validate: (value) => (value && isValidPhoneNumber(value)) || 'Enter a valid phone number.',
        }}
        render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => (
          <FormControl mb='12px' isRequired isInvalid={!!error}>
            <FormLabel fontSize='12px'>Contact phone</FormLabel>
            <PhoneInput
              international
              defaultCountry={DEFAULT_COUNTRY_CODE}
              placeholder='5X XXX XXXX'
              value={value}
              onChange={(next) => onChange(next ?? '')}
              onBlur={onBlur}
              name={name}
              ref={ref}
              inputComponent={Input}
            />
            <FormErrorMessage>{error?.message}</FormErrorMessage>
          </FormControl>
        )}
      />

      <FormControl mb='16px'>
        <FormLabel fontSize='12px'>Country</FormLabel>
        <Flex
          align='center'
          h='40px'
          px='12px'
          borderRadius='6px'
          border='1px solid'
          borderColor='gray.200'
          bg='gray.50'
          aria-readonly='true'>
          <Text fontSize='14px' color='gray.600'>
            {DEFAULT_COUNTRY_NAME}
          </Text>
        </Flex>
      </FormControl>

      <Box
        as='button'
        type='submit'
        disabled={isCreating}
        w='full'
        py='12px'
        borderRadius='12px'
        bg='red.600'
        opacity={isCreating ? 0.6 : 1}
        _hover={{ bg: 'red.700' }}>
        <Text fontSize='15px' fontWeight='bold' color='white'>
          {isCreating ? 'Adding…' : 'Add & select'}
        </Text>
      </Box>
    </Box>
  );
};
